import type {
  AdminApiVoiceUsage,
  VoiceCreditUsageByAgent,
} from "@repo/app-types/entities/creditUsage";
import { TableSkeleton } from "@repo/ui/Skeletons/skeletons";
import type { FC } from "react";
import { useVoiceUsageQuery } from "../../../api/entities/company.js";
import { SettingsTable } from "../shared/settingsTable.js";
import { SettingsTableBodySection } from "../shared/settingsTableBodySection.js";
import { SettingsTableDataCell } from "../shared/settingsTableDataCell.js";
import { SettingsTableHeaderCell } from "../shared/settingsTableHeaderCell.js";
import { SettingsTableHeaderSection } from "../shared/settingsTableHeaderSection.js";
import {
  CreditUsageCard,
  CreditUsageCardSkeleton,
  useCreditUsageSection,
} from "./creditUsageSection.js";
import {
  formatPeriodLabel,
  formatTermEndDate,
  formatUsageAgainstIncluded,
  formatWholeNumber,
} from "./usageFormatting.js";

interface VoiceCreditsSectionProps {
  periodOffset: number;
}

/**
 * The voice agent section of the billing page: the period's voice credit
 * balance and usage per agent, for the period the page-level header selects.
 * Voice credits are their own balance, bought as voice packs, so the section
 * speaks in voice credits throughout.
 */
export const VoiceCreditsSection: FC<VoiceCreditsSectionProps> = (props) => {
  const { periodOffset } = props;
  const { selectedUsage, isUsageLoading } = useCreditUsageSection(
    useVoiceUsageQuery,
    periodOffset,
  );
  // Without a known term the section falls back to the period's own usage.
  const term = selectedUsage?.term ?? null;
  const agents = term?.agents ?? selectedUsage?.agents ?? [];

  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-base font-semibold text-foreground">
          Voice agent credits
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Used only by voice agents and purchased separately from monthly AI
          credits.
        </p>
      </div>
      {selectedUsage && !isUsageLoading ? (
        <UsageOverview usage={selectedUsage} />
      ) : (
        <CreditUsageCardSkeleton />
      )}
      {/* Only agents Metronome metered are listed, so an empty list is a
          period with no voice usage to break down. */}
      {!isUsageLoading && agents.length === 0 ? null : (
        <UsageByAgent agents={agents} isLoading={isUsageLoading} />
      )}
    </section>
  );
};

interface UsageOverviewProps {
  usage: AdminApiVoiceUsage;
}

/**
 * Voice credits are bought in packs, never included with a license. A pack is
 * granted per term and spent across every period in it, so the card paces what
 * the pack has paid for through the selected period against what it granted.
 * With nothing to pace against — no pack covering the period, no known term,
 * no readable balance — it reports the selected period's metered usage alone.
 */
const UsageOverview: FC<UsageOverviewProps> = (props) => {
  const { usage } = props;
  const { term, balances } = usage;

  const isCurrentPeriod = usage.selected_period.offset === 0;
  const periodLabel = isCurrentPeriod
    ? null
    : formatPeriodLabel(usage.selected_period);

  const usageOnlyCard = (
    <CreditUsageCard
      title="Voice credits"
      headline={`${formatWholeNumber(usage.credits_used)} used`}
      details={[formatPeriodLabel(usage.selected_period)]}
    />
  );
  if (term === null || balances.kind === "unknown") {
    return usageOnlyCard;
  }
  // A pack's grant covers its whole term, so no grant over the selected period
  // means a period the pack did not exist for — before it was bought, or after
  // a term whose pack expired.
  const capacity = balances.granted_purchased_credits;
  if (capacity === 0) {
    return usageOnlyCard;
  }

  // What the pack itself paid for. Metered usage covered by another credit,
  // or left uncovered, never draws the balance down and so never counts here.
  const remaining = balances.remaining_purchased_credits;
  const spent = capacity - remaining;

  return (
    <CreditUsageCard
      title="Voice credits"
      headline={`${formatWholeNumber(remaining)} left`}
      bar={{
        percentage: (spent / capacity) * 100,
        isExhausted: remaining === 0,
      }}
      details={[
        formatUsageAgainstIncluded(
          spent,
          capacity,
          "voice credits used this term",
        ),
        // A pack is granted per contract term, so the billing period the page
        // selects is not when these credits expire.
        isCurrentPeriod
          ? `Expires ${formatTermEndDate(term.ends_at)}`
          : periodLabel,
      ]}
    />
  );
};

interface UsageByAgentProps {
  agents: readonly VoiceCreditUsageByAgent[];
  isLoading: boolean;
}

const UsageByAgent: FC<UsageByAgentProps> = (props) => {
  const { agents, isLoading } = props;

  return (
    <SettingsTable>
      <caption className="sr-only">Usage by agent</caption>
      <SettingsTableHeaderSection>
        <tr>
          <SettingsTableHeaderCell className="w-[70%]">
            Voice agent
          </SettingsTableHeaderCell>
          <SettingsTableHeaderCell className="w-[30%] text-right">
            Voice credits used
          </SettingsTableHeaderCell>
        </tr>
      </SettingsTableHeaderSection>
      <SettingsTableBodySection>
        {isLoading ? <TableSkeleton /> : null}
        {agents.map((agent) => (
          <tr className="hover:bg-gray-50" key={agent.voice_agent_id}>
            <SettingsTableDataCell>
              {agent.voice_agent_name || "Unnamed agent"}
            </SettingsTableDataCell>
            <SettingsTableDataCell className="text-right tabular-nums">
              {formatWholeNumber(agent.credits_used)}
            </SettingsTableDataCell>
          </tr>
        ))}
      </SettingsTableBodySection>
    </SettingsTable>
  );
};
